import type { ParsedTicketData } from '../components/UploadTicket/UploadTicket';

export interface ParsedTicketFormSetters {
  setTicketNumber: (value: string) => void;
  setTicketCitation: (value: string) => void;
  setTicketAddress: (value: string) => void;
  setTicketCity: (value: string) => void;
  setTicketCounty: (value: string) => void;
  setTicketCourtDate: (value: string) => void;
  setTicketLicenseType: (value: string) => void;
  /**
   * Optional so the two existing callers can adopt these one at a time. A caller that does not pass
   * a setter simply keeps today's behaviour for that field — the value is still on the response.
   */
  setTicketRespondByDate?: (value: string) => void;
  setTicketArrestingAgency?: (value: string) => void;
  setTicketAgencyCaseNumber?: (value: string) => void;
  setTicketLicensePlate?: (value: string) => void;
}

/** YYYY-MM-DD, or the original string when it is not a date we can read. */
function toDateInputValue(raw: string): string {
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? raw : parsed.toISOString().split('T')[0];
}

export function applyParsedTicketData(
  parsedData: ParsedTicketData,
  setters: ParsedTicketFormSetters,
): void {
  if (parsedData.Ticket_Number)
    setters.setTicketNumber(parsedData.Ticket_Number);
  if (parsedData.Ticket_Citation)
    setters.setTicketCitation(parsedData.Ticket_Citation);
  if (parsedData.Ticket_Address)
    setters.setTicketAddress(parsedData.Ticket_Address);
  if (parsedData.Ticket_City) setters.setTicketCity(parsedData.Ticket_City);
  if (parsedData.Ticket_County)
    setters.setTicketCounty(parsedData.Ticket_County);
  if (parsedData.Ticket_LicenseType)
    setters.setTicketLicenseType(parsedData.Ticket_LicenseType);

  // The three ALPR fields. They have been read off the ticket since Aug 24 and discarded on the way
  // to the form, which is why they are empty on every case and why preservation demands go out
  // addressed to "Custodian of Records" over ruled blanks.
  if (parsedData.Ticket_ArrestingAgency)
    setters.setTicketArrestingAgency?.(parsedData.Ticket_ArrestingAgency);
  if (parsedData.Ticket_AgencyCaseNumber)
    setters.setTicketAgencyCaseNumber?.(parsedData.Ticket_AgencyCaseNumber);
  if (parsedData.Ticket_LicensePlate)
    setters.setTicketLicensePlate?.(parsedData.Ticket_LicensePlate);

  if (parsedData.Ticket_CourtDate) {
    setters.setTicketCourtDate(toDateInputValue(parsedData.Ticket_CourtDate));
  }

  // Respond-by is a REQUIRED field on the New Ticket form that the scan never filled, so it has been
  // typed by hand on every upload. Deliberately NOT falling back to the court date: they are
  // different dates, and copying one into the other would put a deadline on the case that nobody
  // printed.
  if (parsedData.Ticket_RespondBy) {
    setters.setTicketRespondByDate?.(toDateInputValue(parsedData.Ticket_RespondBy));
  }
}
